import { QCBadge, StatusBadge } from '@/components/UI';
import Progress from '@nautilusbio/nb-design-system/Progress';
import { Stack, type StackProps } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { useElapsedRunTime } from '../../hooks';
import { isInstrumentRunActive } from '../../runStatus';
import type { InstrumentRunPublic } from '../../types';

import styles from './styles.module.css';

type Props = {
  run: InstrumentRunPublic;
};

const sharedStackProps: StackProps = {
  alignItems: 'center',
  gap: 2,
  justifyContent: 'center',
  orientation: 'vertical',
};

const RunStatusGrid = ({ run }: Props) => {
  const { t } = useTranslation('runs', { keyPrefix: 'header' });
  const isActive = isInstrumentRunActive(run);
  const elapsed = useElapsedRunTime(run.started_at, isActive);

  return (
    <Stack className={styles.statusGrid} orientation="horizontal">
      <Stack className={styles.statusColumn} {...sharedStackProps}>
        <Text as="span" className={styles.statusLabel}>
          {t('instrument_status')}
        </Text>

        {run.status ? <StatusBadge className={styles.qc} status={run.status} /> : <Text size="sm">—</Text>}
      </Stack>

      <Stack className={styles.statusColumn} {...sharedStackProps}>
        <Text as="span" className={styles.statusLabel}>
          {t('decode_status')}
        </Text>

        {run.decode_status ? (
          <StatusBadge className={styles.qc} status={run.decode_status} />
        ) : (
          <Text size="sm">—</Text>
        )}
      </Stack>

      <Stack className={styles.statusColumn} {...sharedStackProps}>
        <Text as="span" className={styles.statusLabel}>
          {t('run_qc')}
        </Text>

        {/* All three status fields are non-nullable; the guards above are vestigial. */}
        <QCBadge className={styles.qc} qc={run.run_qc_outcome} />
      </Stack>

      {/* No expected_run_time on the API yet — indeterminate spinner + elapsed clock only. */}
      {isActive && elapsed && (
        <Stack className={styles.statusColumn} data-testid="run-time-column" {...sharedStackProps}>
          <Text as="span" className={styles.statusLabel}>
            {t('run_time')}
          </Text>

          <Stack alignItems="center" gap={2} orientation="horizontal">
            <Progress data-testid="run-progress-ring" indeterminate size="sm" variant="circular" visual="primary" />
            <Text size="sm">{elapsed}</Text>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default memo(RunStatusGrid);
